import type { Dispatch, SetStateAction } from 'react'
import { datadogRum } from '@datadog/browser-rum-slim'

import type { OrderForm } from '../entity/orderForm'
import type { OrderItemOnAdd, OrderItemOnUpdate } from '../data/types'
import { addCartItems, updateCartItems } from '../data/checkout'

export const useCartItems = (
  orderForm: OrderForm,
  setOrderForm: Dispatch<SetStateAction<OrderForm>>,
  setIsLoading: Dispatch<SetStateAction<boolean>>
) => {
  const addItems = async (items: OrderItemOnAdd[], orderFormId?: string) => {
    if (!orderForm) {
      return
    }

    setIsLoading(true)

    try {
      const updatedOrderform = await addCartItems(
        orderFormId ?? orderForm.orderFormId,
        items
      )

      setOrderForm(updatedOrderform)

      for (const item of items) {
        const addedItem = updatedOrderform?.items?.find((i) => i.id === item.id)

        datadogRum.addAction('add_to_cart', {
          product_id: item.id,
          product_name: addedItem?.productName ?? addedItem?.name,
          category: addedItem?.category,
          brand: addedItem?.brand,
          price: addedItem?.price,
          quantity: item.quantity,
        })
      }
    } catch (error) {
      const err = error as Error

      for (const item of items) {
        datadogRum.addError(new Error('add_to_cart_failed'), {
          product_id: item.id,
          error_message: err?.message,
        })
      }

      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const updateItem = async (itemToBeUpdated: OrderItemOnUpdate) => {
    setIsLoading(true)

    try {
      const items = orderForm?.items?.map((item) => {
        if (item.itemIndex === itemToBeUpdated.index) {
          return {
            index: itemToBeUpdated.index,
            quantity: itemToBeUpdated.quantity,
          }
        }

        return {
          index: item.itemIndex,
          quantity: item.quantity,
        }
      })

      const updatedOrderform = await updateCartItems(
        orderForm.orderFormId,
        items
      )

      setOrderForm(updatedOrderform)

      return updatedOrderform
    } finally {
      setIsLoading(false)
    }
  }

  const updateItems = async (itemsToBeUpdated: OrderItemOnUpdate[]) => {
    setIsLoading(true)

    try {
      const updatedItems = orderForm?.items?.map((item) => {
        const itemToUpdate = itemsToBeUpdated.find(
          (update) => update.index === item.itemIndex
        )

        if (itemToUpdate) {
          return {
            index: itemToUpdate.index,
            quantity: itemToUpdate.quantity,
          }
        }

        return {
          index: item.itemIndex,
          quantity: item.quantity,
        }
      })

      const updatedOrderform = await updateCartItems(
        orderForm.orderFormId,
        updatedItems
      )

      setOrderForm(updatedOrderform)
    } finally {
      setIsLoading(false)
    }
  }

  return { addItems, updateItem, updateItems }
}
